import { useReducer, useRef, useState } from 'react'
import 'react-image-lightbox/style.css'

import { LotImageTypes, LotResponseImage } from '@nx/api'
import { useNewImageSubdomain } from '@nx/helpers'

import { Carousel } from './Carousel'
import { CarouselWrapper, StyledLightbox } from './LightboxCarousel.styles'
import {
  CarouselSlide,
  SlideType,
  SliderAction,
  SliderState,
} from './LightboxCarousel.types'

export interface LightboxCarouselProps {
  images: LotResponseImage[]
  videos?: string[]
  description: string
}

export function LightboxCarousel({
  images,
  videos = [],
  description,
}: LightboxCarouselProps) {
  const totalSlides = images.length + videos.length

  const [isLightBoxOpen, setIsLightBoxOpen] = useState(false)
  const moveToCarouselItemRef = useRef<(page: number) => void>()

  const carouselSlides = images
    .map((image: LotResponseImage) => {
      const imageUse = image.imageuses?.find(
        ({ sImageUse, iImageNo_id }) =>
          sImageUse === LotImageTypes.main && image.iImageNo === iImageNo_id
      )

      const param = imageUse?.sURLParams ? `&${imageUse.sURLParams}` : ''

      return { src: `${image.image_url}${param}`, caption: image.sCredit }
    })
    .filter(({ src }) => src)
    .map<CarouselSlide>(({ src, caption }, index) => ({
      src,
      alt: `${description} image ${index + 1}${caption && ` - ${caption}`}`,
      type: SlideType.image,
      caption,
    }))

  if (videos.length > 0) {
    carouselSlides.splice(
      1,
      0,
      ...videos.map<CarouselSlide>((video, index) => ({
        src: video,
        alt: `${description} video ${index + 1}`,
        type: SlideType.video,
        caption: '',
      }))
    )
  }

  const [slideState, slideDispatch] = useReducer(
    slideReducer,
    totalSlides,
    initSliderState
  )
  const { addNewImageSubdomain } = useNewImageSubdomain()

  if (totalSlides !== slideState.totalSlides) {
    slideDispatch({
      type: 'UPDATE_TOTAL',
      payload: { totalSlides },
    })
  }

  return (
    <>
      {isLightBoxOpen && (
        <StyledLightbox
          mainSrc={addNewImageSubdomain(
            getLightboxImage(
              'currentSlide',
              totalSlides,
              slideState,
              carouselSlides
            )
          )}
          prevSrc={addNewImageSubdomain(
            getLightboxImage(
              'prevSlide',
              totalSlides,
              slideState,
              carouselSlides
            )
          )}
          nextSrc={addNewImageSubdomain(
            getLightboxImage(
              'nextSlide',
              totalSlides,
              slideState,
              carouselSlides
            )
          )}
          clickOutsideToClose={false}
          onCloseRequest={() => {
            setIsLightBoxOpen(false)
          }}
          onMovePrevRequest={() => {
            // The carousel ends from an index of N and the lightbox an index of N-1.
            // slideState.currentSlide is relative to the lightbox
            slideDispatch({
              type: 'UPDATE_SLIDE',
              payload: { newSlide: slideState.currentSlide - 1 },
            })
            moveToCarouselItemRef.current?.(slideState.currentSlide)
          }}
          onMoveNextRequest={() => {
            // The carousel starts from an index of 1 and the lightbox an index of 0
            // slideState.currentSlide is relative to the lightbox
            slideDispatch({
              type: 'UPDATE_SLIDE',
              payload: { newSlide: slideState.currentSlide + 1 },
            })
            moveToCarouselItemRef.current?.(slideState.currentSlide + 2)
          }}
        />
      )}
      {totalSlides > 0 && (
        <CarouselWrapper $activeThumbnail={slideState.currentSlide}>
          <Carousel
            moveToItemRef={moveToCarouselItemRef}
            carouselSlides={carouselSlides}
            videos={videos}
            description={description}
            setIsLightBoxOpen={setIsLightBoxOpen}
            slideDispatch={slideDispatch}
          />
        </CarouselWrapper>
      )}
    </>
  )
}

function getLightboxImage(
  key: 'currentSlide' | 'prevSlide' | 'nextSlide',
  totalSlides: number,
  sliderState: SliderState,
  carouselSlides: CarouselSlide[]
) {
  const slideIndex = sliderState[key]
  if (typeof slideIndex === 'undefined') return ''

  const slide = carouselSlides[slideIndex]
  if (key === 'currentSlide') {
    return slide.type === SlideType.image
      ? slide.src
      : `https://content.jwplatform.com/thumbs/${slide.src}.jpg`
  }

  if (totalSlides <= 1) return ''

  return slide.type === SlideType.image
    ? slide.src
    : `https://content.jwplatform.com/thumbs/${slide.src}.jpg`
}

const initSliderState = (totalSlides: number): SliderState => {
  return {
    currentSlide: 0,
    prevSlide: undefined,
    nextSlide: 1,
    totalSlides,
  }
}

export const slideReducer = (state: SliderState, action: SliderAction) => {
  switch (action.type) {
    case 'UPDATE_SLIDE': {
      const { newSlide } = action.payload

      if (newSlide === 0) {
        return {
          ...state,
          currentSlide: newSlide,
          nextSlide: newSlide + 1,
          prevSlide: undefined,
        }
      } else if (newSlide === state.totalSlides - 1) {
        return {
          ...state,
          currentSlide: newSlide,
          nextSlide: undefined,
          prevSlide: newSlide - 1,
        }
      } else {
        return {
          ...state,
          currentSlide: newSlide,
          nextSlide: newSlide + 1,
          prevSlide: newSlide - 1,
        }
      }
    }

    case 'UPDATE_TOTAL': {
      const { totalSlides } = action.payload
      const oneSlideOrLess = totalSlides <= 1

      return {
        currentSlide: 0,
        prevSlide: undefined,
        nextSlide: oneSlideOrLess ? undefined : 1,
        totalSlides,
      }
    }
  }
}
